import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { getIrsDefaults, type IrsDefaults, updateIrsDefaults } from "@/features/reference/api";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  interestRate: Yup.string()
    .matches(/^\d+(\.\d{1,4})?$/, "Percent, e.g. 8.00")
    .required("Required"),
  passportThreshold: Yup.string()
    .matches(/^\d+(\.\d{1,2})?$/, "Amount, e.g. 62000")
    .required("Required"),
  vehicleExtraMiles: Yup.number().integer().min(0).required("Required"),
  vehicleExtraYears: Yup.number().integer().min(0).required("Required"),
  vehicleExtraAmount: Yup.string()
    .matches(/^\d+(\.\d{1,2})?$/, "Amount, e.g. 200")
    .required("Required"),
});

export function IrsDefaultsTab() {
  const [initial, setInitial] = useState<IrsDefaults | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getIrsDefaults()
      .then(setInitial)
      .catch(() => setError(true));
  }, []);

  if (error) return <p className="text-destructive text-sm">Couldn&apos;t load IRS defaults.</p>;
  if (!initial) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <div className="max-w-lg">
      <p className="text-muted-foreground mb-4 text-sm">
        Scalar defaults the calculators read (interest accrual, passport-revocation flag, and the
        vehicle &quot;extra&quot;-allowance thresholds). Not vintaged — this is a live singleton.
      </p>
      <Formik
        initialValues={initial}
        validationSchema={schema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const saved = await updateIrsDefaults({
              ...values,
              vehicleExtraMiles: Number(values.vehicleExtraMiles),
              vehicleExtraYears: Number(values.vehicleExtraYears),
            });
            setInitial(saved);
            toast.success("IRS defaults saved");
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Couldn't save the defaults.");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="grid gap-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField name="interestRate" label="Interest rate (%)" placeholder="8.00" inputMode="decimal" />
              <FormField
                name="passportThreshold"
                label="Passport threshold ($)"
                placeholder="62000"
                inputMode="decimal"
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              <FormField name="vehicleExtraMiles" label="Vehicle extra: miles" type="number" />
              <FormField name="vehicleExtraYears" label="Vehicle extra: years" type="number" />
              <FormField
                name="vehicleExtraAmount"
                label="Vehicle extra: $"
                placeholder="200"
                inputMode="decimal"
              />
            </div>
            <div>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : "Save defaults"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
